import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { type Resolver } from 'react-hook-form'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { XIcon } from 'lucide-react'
import {
	Button, ConfirmDialog, IconButton,
	Dialog, DialogContent, DialogTitle,
	Form,
	StageElement, Stages,
} from '@rentflowdev/ui'
import {
	useCreatePropertyMutation, useUpdatePropertyMutation, useUpdatePropertyStatusMutation, useGetPropertyQuery,
	useUpsertRentTermsMutation,
	useUpsertPropertyUtilityMutation, useDeletePropertyUtilityMutation,
	useCreateAdditionalServiceMutation, useUpdateAdditionalServiceMutation, useDeleteAdditionalServiceMutation,
	useAddPropertyImageMutation, useRemovePropertyImageMutation,
} from '@/api'
import { CURRENCIES } from '@/types'
import type { CurrencyCode, Property } from '@/types'
import { useAccountCurrency } from '@/lib/use-account-currency'
import { usePersistedForm } from '@/lib/use-persisted-form'
import {
	addPropertySchema, DEFAULT_VALUES,
	STEP_1_FIELDS, STEP_2_FIELDS, STEP_3_FIELDS, STEP_4_FIELDS, STEP_5_FIELDS,
	type AddPropertyFormValues,
} from './schema'

const DRAFT_KEY = 'property:add'

/**
 * The utility types this wizard renders. `UtilityType` in the API also covers
 * GAS, INTERNET and OTHER — rows this form neither shows nor writes, so it
 * must leave them alone rather than treat their absence as "delete".
 */
const WIZARD_UTILITY_TYPES = ['ELECTRICITY', 'WATER', 'HEATING', 'WASTE'] as const

import { propertyToFormValues } from './from-property'
import { Step1Basics } from './step-1-basics'
import { Step2Details } from './step-2-details'
import { Step3RentTerms } from './step-3-rent-terms'
import { Step4Utilities } from './step-4-utilities'
import { Step5AdditionalServices } from './step-5-additional-services'
import { Step6PhotosDescription } from './step-6-photos-description'
import { PropertySavedFlash, type PropertySavedFlashVariant } from './property-saved-flash'

/** Per-step validators, indexed by step number (0-based). Last step (5) has no validator — it's the final submit. */
const STEP_VALIDATORS = [
	STEP_1_FIELDS,
	STEP_2_FIELDS,
	STEP_3_FIELDS,
	STEP_4_FIELDS,
	STEP_5_FIELDS,
] as const

/**
 * Step a reopened draft should land on: the first one still holding a
 * validation error. Reuses `addPropertySchema` rather than a hand-written
 * "is this filled in" check, so type-conditional requirements — a LAND needs
 * far less than an APARTMENT — are honoured for free. A complete draft opens
 * at the beginning, since there is nothing to resume.
 */
function firstIncompleteStep(values: unknown): number {
	const parsed = addPropertySchema.safeParse(values)
	if (parsed.success) return 0
	const failed = new Set(parsed.error.issues.map((i) => String(i.path[0])))
	const idx = STEP_VALIDATORS.findIndex((fields) => fields.some((f) => failed.has(f)))
	return idx === -1 ? 0 : idx
}

interface AddPropertyDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	/**
	 * Continue an existing DRAFT instead of creating from scratch (#285). The
	 * form is seeded from the stored property and every save updates that row
	 * rather than adding another one.
	 */
	propertyId?: string
}

const WIZARD_STEPS = [
	'Основная информация',
	'Детали',
	'Условия аренды',
	'Коммунальные платежи',
	'Дополнительные услуги',
	'Фото и описание',
] as const

function composePropertyTitle(values: Partial<AddPropertyFormValues>, t: (key: string) => string): string {
	const typeLabel = values.type ? t(`properties.types.${values.type}`) : ''
	const roomsLabel = values.rooms ? `${values.rooms}-к` : ''
	const address = values.address?.trim() ?? ''
	return [typeLabel, roomsLabel, address].filter(Boolean).join(' ') || 'Объект'
}

const toNum = (v: unknown): number | undefined => {
	if (v === undefined || v === null || v === '') return undefined
	const n = Number(v)
	return Number.isFinite(n) ? n : undefined
}

const elevatorCount = (v: 'NONE' | '1' | '2' | '3' | '4' | undefined): number | undefined => {
	if (v === undefined) return undefined
	return v === 'NONE' ? 0 : Number(v)
}

// Robust against raw form values: form.getValues() can return '' for a
// touched-then-cleared numeric input, which must be omitted, not sent as ''.
const decStr = (v: number | string | undefined | null): string | undefined => {
	const n = toNum(v)
	return n != null ? String(n) : undefined
}

// Every amount in the payload is denominated in one currency. It is applied
// here rather than read out of the form because the form no longer asks: one
// setting beats four independent pickers that could disagree about the same
// property. For a NEW property that setting is the account currency; an
// existing one keeps the currency its amounts were agreed in (see
// payloadCurrency below).
function buildPropertyPayload(values: AddPropertyFormValues, currency: CurrencyCode) {
	const utilities = WIZARD_UTILITY_TYPES.flatMap((type) => {
		const u = values.utilities?.[type]
		if (!u || !u.calculation) return []
		return [{
			type,
			calculation:      u.calculation,
			paidBy:           u.paidBy ?? 'TENANT',
			hasSeparateMeter: u.hasSeparateMeter ?? false,
			meterNumber:      u.meterNumber || undefined,
			ratePerSqm:       decStr(u.ratePerSqm),
			fixedAmount:      decStr(u.fixedAmount),
			coefficient:      decStr(u.coefficient),
			propertyArea:     decStr(u.propertyArea),
			buildingArea:     decStr(u.buildingArea),
			currency:         currency,
		}]
	})

	const additionalServices = (values.additionalServices ?? []).flatMap((s) => {
		if (!s.name || s.pricePerUnit == null || s.quantity == null || !s.unit) return []
		return [{
			name:         s.name,
			pricePerUnit: String(s.pricePerUnit),
			currency:     currency,
			quantity:     String(s.quantity),
			unit:         s.unit,
			provisionType: s.provisionType ?? 'OWN',
			vatIncluded:   s.vatIncluded ?? true,
		}]
	})

	const areaNum = toNum(values.totalArea) ?? 0

	return {
		phone:           values.phone || undefined,
		ownerType:       values.ownerType,
		apartmentNumber: values.apartmentNumber || undefined,
		housingType:     values.housingType,
		roomLocation:     values.roomLocation,
		roomsInApartment: toNum(values.roomsInApartment),
		roomHousingType:  values.roomHousingType,
		bedCount:         toNum(values.bedCount),
		sleepingPlaces:   toNum(values.sleepingPlaces),
		eventsAllowed:    values.eventsAllowed,
		fireplace:        values.fireplace,
		houseKind:            values.houseKind,
		wallsType:            values.wallsType,
		sauna:                values.sauna,
		pool:                 values.pool,
		terrace:              values.terrace,
		plotArea:             decStr(values.plotArea),
		landCategory:         values.landCategory,
		bathroomLocations:    values.bathroomLocations,
		communications:       values.communications,
		transportAccess:      values.transportAccess,
		nearbyInfrastructure: values.nearbyInfrastructure,
		type:        values.type,
		address:     values.address,
		city:        values.city,
		latitude:    toNum(values.latitude),
		longitude:   toNum(values.longitude),
		description: values.description?.trim() || undefined,

		area:         areaNum ? String(areaNum) : undefined,
		livingArea:   decStr(values.livingArea),
		kitchenSpace: decStr(values.kitchenSpace),
		rooms:        toNum(values.rooms),
		roomType:     values.roomType,
		floor:       toNum(values.floor),
		totalFloors: toNum(values.totalFloors),
		yearBuilt:   toNum(values.yearBuilt),

		buildingType:  values.buildingType,
		renovation:    values.renovation,
		bathroom:      values.bathroomType,

		balcony:  values.balcony,
		loggia:   values.loggia,
		wardrobe: values.wardrobe,

		passengerElevator: elevatorCount(values.passengerElevators),
		freightElevator:   elevatorCount(values.freightElevators),
		parking:           values.parking,
		parkingType:       values.parkingType,
		yard:              values.yard,

		concierge: values.concierge,
		chute:     values.chute,
		gas:       values.gas,

		furniture:  values.furniture,
		appliances: values.appliances,
		internet:   values.internet,

		maxTenants:      toNum(values.maxTenants),
		childrenAllowed: values.childrenAllowed,
		petsAllowed:     values.petsAllowed,
		smokingAllowed:  values.smokingAllowed,

		// Commercial premises. Sent regardless of type: the server clears them
		// when a property stops being commercial, so the form does not have to
		// know which ones belong to which type twice over.
		onlineViewing:          values.onlineViewing,
		multipleFloors:         values.multipleFloors,
		officeLayouts:          values.officeLayouts,
		partialRent:            values.partialRent,
		ceilingHeight:          decStr(values.ceilingHeight),
		finishing:              values.finishing,
		powerCapacity:          decStr(values.powerCapacity),
		powerCapacityExpandable: values.powerCapacityExpandable,
		commercialBuildingType: values.commercialBuildingType,
		buildingClass:          values.buildingClass,
		commercialParking:      values.commercialParking,
		parkingFree:            values.parkingFree,
		parkingForTrucks:       values.parkingForTrucks,
		parkingSpaces:          toNum(values.parkingSpaces),
		entrance:               values.entrance,
		separateEntrance:       values.separateEntrance,
		commercialHeating:      values.commercialHeating,
		retailPlacement:        values.retailPlacement,
		additionalCategories:   values.additionalCategories,
		garageKind:             values.garageKind,
		garageType:             values.garageType,
		parkingSpaceType:       values.parkingSpaceType,
		security:               values.security,
		seatsCount: toNum(values.seatsCount),
		weekendsOpen: values.weekendsOpen,
		aroundTheClock: values.aroundTheClock,
		workplaceStatus: values.workplaceStatus,
		privateOfficeArea: decStr(values.privateOfficeArea),
		privateOfficeSeats: toNum(values.privateOfficeSeats),
		amenitiesInclusion: values.amenitiesInclusion,
		keyAmenities: values.keyAmenities,
		equipment: values.equipment,
		foodAndDrinks: values.foodAndDrinks,
		coworkingServices: values.coworkingServices,
		coworkingExtras: values.coworkingExtras,

		rentTerms: values.rentAmount != null && values.rentAmount > 0 ? {
			rentAmount:       String(values.rentAmount),
			rentCurrency:     currency,
			rentPeriod:       values.periodicity,
			vatIncluded:      values.vatIncluded,
			depositAmount:    decStr(values.depositAmount),
			// Своя, если о ней договорились: депозит бывает в другой валюте, и
			// колонка под него в базе отдельная. Подставлять сюда валюту ставки
			// значило переписывать чужую договорённость при каждом пересохранении.
			depositCurrency:  values.depositAmount != null ? (values.depositCurrency ?? currency) : undefined,
			penaltyRate:      decStr(values.penaltyPercent),
			penaltyGraceDays: toNum(values.penaltyGraceDays),
			otherUtilitiesPayment: decStr(values.otherUtilitiesPayment),

			rentUnit:                  values.rentUnit,
			agentCommissionPercent:    decStr(values.agentCommissionPercent),
			leaseKind:                 values.leaseKind,
			rentHolidays:              values.rentHolidays,
			minLeaseMonths:            toNum(values.minLeaseMonths),
			utilitiesIncluded:         values.utilitiesIncluded,
			operatingExpensesIncluded: values.operatingExpensesIncluded,
			depositMonths:             decStr(values.depositMonths),
		} : undefined,

		utilities,
		additionalServices,

		videoUrl: values.videoUrl?.trim() || undefined,
		images: (values.images ?? []).map((url, order) => ({ url, order })),
	}
}

export function AddPropertyDialog({ open, onOpenChange, propertyId }: AddPropertyDialogProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const isEditing = !!propertyId
	const accountCurrency = useAccountCurrency()
	const { data: draftProperty } = useGetPropertyQuery(propertyId ?? '', { skip: !propertyId })
	// The profile currency seeds brand-new properties only. Re-saving an
	// existing one (edit, reopened draft) keeps the currency its amounts were
	// agreed in — stamping today's setting over it would silently re-denominate
	// every saved figure.
	const storedCurrency = draftProperty?.rentTerms?.rentCurrency
	const payloadCurrency: CurrencyCode =
		storedCurrency && (CURRENCIES as readonly string[]).includes(storedCurrency)
			? (storedCurrency as CurrencyCode)
			: accountCurrency
	const [flashType,         setFlashType]         = useState<PropertySavedFlashVariant | null>(null)
	const [savedTitle,        setSavedTitle]        = useState('')
	const [savedId,           setSavedId]           = useState<string | null>(null)
	const [errorDetail,       setErrorDetail]       = useState<string | null>(null)
	const [isDraftSaving,     setIsDraftSaving]     = useState(false)
	const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
	const [createProperty, { isLoading: isCreating }] = useCreatePropertyMutation()
	const [updateProperty, { isLoading: isUpdating }] = useUpdatePropertyMutation()
	const [updateStatus,   { isLoading: isActivating }] = useUpdatePropertyStatusMutation()
	// Child collections travel through their own endpoints on the edit path —
	// see `syncChildCollections`.
	const [upsertRentTerms] = useUpsertRentTermsMutation()
	const [upsertUtility]   = useUpsertPropertyUtilityMutation()
	const [deleteUtility]   = useDeletePropertyUtilityMutation()
	const [createService]   = useCreateAdditionalServiceMutation()
	const [updateService]   = useUpdateAdditionalServiceMutation()
	const [deleteService]   = useDeleteAdditionalServiceMutation()
	const [addImage]        = useAddPropertyImageMutation()
	const [removeImage]     = useRemovePropertyImageMutation()
	const [isSyncingChildren, setIsSyncingChildren] = useState(false)
	const isSaving = isCreating || isUpdating || isActivating || isSyncingChildren

	/**
	 * `PATCH /properties/:id` cannot carry rent terms, utilities, additional
	 * services or images: `UpdatePropertyBody` omits them and the gateway's
	 * `whitelist: true` pipe strips them from the body. Creating a property
	 * sends them inline, so the edit path has to write them through their own
	 * endpoints — otherwise everything entered in steps 3-6 is silently
	 * discarded the moment a draft is reopened and saved.
	 */
	async function syncChildCollections(
		id: string,
		payload: ReturnType<typeof buildPropertyPayload>,
		existing: Property | undefined,
		values: AddPropertyFormValues,
	) {
		if (payload.rentTerms) {
			await upsertRentTerms({ id, body: payload.rentTerms }).unwrap()
		}

		// Utilities upsert on (propertyId, type); a type the form no longer
		// bills has to be deleted explicitly. Only the four types the wizard
		// actually edits are eligible for deletion — UtilityType also covers
		// GAS, INTERNET and OTHER, which this form never shows and must not
		// silently drop.
		const nextUtilityTypes = new Set<string>(payload.utilities.map((u) => u.type))
		const wizardUtilityTypes = new Set<string>(WIZARD_UTILITY_TYPES)
		for (const utility of payload.utilities) {
			await upsertUtility({ id, body: utility }).unwrap()
		}
		for (const utility of existing?.utilities ?? []) {
			if (wizardUtilityTypes.has(utility.type) && !nextUtilityTypes.has(utility.type)) {
				await deleteUtility({ id, utilityId: utility.id }).unwrap()
			}
		}

		// Services carry no natural key, so they are matched by name — renaming
		// one reads as "removed and added", which is the same result.
		const existingServices = existing?.additionalServices ?? []
		const serviceByName = new Map(existingServices.map((s) => [s.name, s]))
		for (const service of payload.additionalServices) {
			const previous = serviceByName.get(service.name)
			if (previous) await updateService({ id, serviceId: previous.id, body: service }).unwrap()
			else await createService({ id, body: service }).unwrap()
		}
		// Из формы, а не из payload: неполная строка отбрасывается при сборке
		// запроса, и удаление по payload считало её удалённой пользователем —
		// сохранённая услуга с пустой единицей измерения исчезала при первом же
		// открытии черновика. Незаполненную строку нужно пропустить, а не
		// принять за команду на удаление.
		const nextServiceNames = new Set(
			(values.additionalServices ?? []).map((s) => s.name).filter(Boolean),
		)
		for (const service of existingServices) {
			if (!nextServiceNames.has(service.name)) {
				await deleteService({ id, serviceId: service.id }).unwrap()
			}
		}

		// Images are identified by url: the form holds urls, the row holds ids.
		// Remove first so a replaced set never briefly exceeds the limit.
		const existingImages = existing?.images ?? []
		const nextUrls = new Set(payload.images.map((i) => i.url))
		for (const image of existingImages) {
			if (!nextUrls.has(image.url)) await removeImage({ id, imageId: image.id }).unwrap()
		}
		const existingUrls = new Set(existingImages.map((i) => i.url))
		for (const image of payload.images) {
			if (!existingUrls.has(image.url)) await addImage({ id, body: image }).unwrap()
		}
	}

	const seeded = useMemo(
		() => (draftProperty ? propertyToFormValues(draftProperty) : DEFAULT_VALUES),
		[draftProperty],
	)

	const openedAtStep = useMemo(
		() => (draftProperty ? firstIncompleteStep(seeded) : 0),
		[draftProperty, seeded],
	)

	const { form, step, setStep, resetDraft } = usePersistedForm<AddPropertyFormValues>({
		// Scoped per property so an in-progress new object and a reopened draft
		// never overwrite one another in localStorage.
		key:            `${DRAFT_KEY}:${propertyId ?? 'new'}`,
		// Только при правке черновика: у нового объекта записи ещё нет, и
		// черновик формы — единственное, что хранит набранное.
		recordVersion:  draftProperty?.updatedAt,
		// Seeding through defaultValues rather than form.reset is deliberate —
		// usePersistedForm treats its own hydration as the single source of
		// truth and warns against resetting from outside.
		defaultValues:  seeded,
		// A reopened draft always seeds from the stored row. Restoring the
		// local copy instead would silently resurrect stale browser state over
		// edits made elsewhere (the edit sheet, another manager, the server's
		// own normalization), with nothing on screen saying so. Drafts are
		// still persisted while the wizard is open — this only decides what
		// wins when it is reopened.
		restoreOnOpen:  !isEditing,
		// Cast needed: schema uses z.coerce.number() and .default(), so zod's Input != Output.
		resolver:       zodResolverI18n(addPropertySchema) as Resolver<AddPropertyFormValues>,
		mode:           'onBlur',
		initialStep:    openedAtStep,
		// Hold hydration until the property has arrived, otherwise the form
		// would seed from blanks and never pick the row up.
		active:         open && (!isEditing || !!draftProperty),
	})

	async function handleNext() {
		if (step !== STEP_VALIDATORS.length) {
			const fields = STEP_VALIDATORS[step]
			const valid = await form.trigger([...fields])
			if (valid) setStep(step + 1)
			return
		}

		const fullyValid = await form.trigger()
		if (!fullyValid) {
			const errors = form.formState.errors
			const failingStep = STEP_VALIDATORS.findIndex((fields) =>
				fields.some((f) => f in errors),
			)
			if (failingStep !== -1) setStep(failingStep)
			return
		}

		const values = form.getValues()
		try {
			// Creating is a single atomic call — status AVAILABLE goes in the body,
			// avoiding a duplicate property on retry and a stranded DRAFT if a
			// follow-up status update fails.
			//
			// Updating cannot be: UpdatePropertyRequest carries no status field,
			// status lives behind PATCH /properties/:id/status. Sending it in the
			// update body is silently dropped — the row stays a DRAFT while the
			// wizard reports success. So a reopened draft is saved first, then
			// activated. If the second call fails it stays a draft, which is
			// recoverable and honest; no duplicate is created either way.
			let created
			if (isEditing) {
				const payload = buildPropertyPayload(values, payloadCurrency)
				created = await updateProperty({ id: propertyId, body: payload }).unwrap()
				// Before activation, not after: activating runs the server-side
				// completeness check, which reads the rent terms written here.
				setIsSyncingChildren(true)
				try {
					await syncChildCollections(propertyId, payload, draftProperty ?? undefined, values)
				} finally {
					setIsSyncingChildren(false)
				}
				await updateStatus({ id: propertyId, body: { status: 'AVAILABLE' } }).unwrap()
			} else {
				created = await createProperty({ ...buildPropertyPayload(values, payloadCurrency), status: 'AVAILABLE' }).unwrap()
			}
			if (!created?.id) {
				setErrorDetail(null)
				setFlashType('error')
				return
			}
			setSavedTitle(composePropertyTitle(values, t))
			setSavedId(created.id)
			resetDraft()
			form.reset(DEFAULT_VALUES)
			setStep(0)
			setFlashType('success')
		} catch (err) {
			console.error('createProperty failed:', JSON.stringify(err, null, 2))
			const e = err as { status?: number | string; data?: { message?: string | string[] }; error?: string }
			const msg = Array.isArray(e?.data?.message)
				? e.data.message.join('; ')
				: (e?.data?.message ?? e?.error ?? String(err))
			setErrorDetail(`[${e?.status ?? '?'}] ${msg}`)
			setFlashType('error')
		}
	}

	function handleBack() {
		if (step > 0) setStep(step - 1)
	}

	function resetAndClose() {
		onOpenChange(false)
		setStep(0)
		setFlashType(null)
		setErrorDetail(null)
		setShowDiscardConfirm(false)
	}

	/**
	 * Close after the user confirmed "the entered data will be lost". Without
	 * clearing the stored draft the data is not lost at all — it comes back on
	 * the next open, which is the opposite of what was confirmed.
	 */
	function discardAndClose() {
		resetDraft()
		form.reset(DEFAULT_VALUES)
		resetAndClose()
	}

	/**
	 * Saves form data to the DB as a DRAFT and closes the dialog.
	 * Returns true if the draft was saved, false if there was not enough data.
	 */
	async function saveDraftToDb(): Promise<boolean> {
		const values = form.getValues()
		if (!values.type || !values.address?.trim() || !values.city?.trim()) {
			return false
		}
		setIsDraftSaving(true)
		try {
			// Update in place when the draft already exists — POSTing again would
			// add a second row on every save.
			const payload = buildPropertyPayload(values, payloadCurrency)
			let created
			if (isEditing) {
				created = await updateProperty({ id: propertyId, body: payload }).unwrap()
				await syncChildCollections(propertyId, payload, draftProperty ?? undefined, values)
			} else {
				created = await createProperty(payload).unwrap()
			}
			setSavedTitle(composePropertyTitle(values, t))
			setSavedId(created?.id ?? null)
			resetDraft()
			form.reset(DEFAULT_VALUES)
			setStep(0)
			setFlashType('draft')
			return true
		} catch {
			return false
		} finally {
			setIsDraftSaving(false)
		}
	}

	function hasFormProgress() {
		// A reopened draft opens at `firstIncompleteStep`, so an absolute step
		// index no longer means the user did anything — compare against the step
		// the wizard opened at instead, or closing an untouched draft saves it.
		return step > openedAtStep
			|| form.formState.isDirty
			|| Object.keys(form.formState.touchedFields).length > 0
	}

	async function handleWizardCloseClick() {
		if (!hasFormProgress()) {
			resetAndClose()
			return
		}
		const saved = await saveDraftToDb()
		if (!saved) {
			setShowDiscardConfirm(true)
		}
	}

	function handleOpenChange(next: boolean) {
		if (!next) {
			if (hasFormProgress() && flashType === null) {
				void handleWizardCloseClick()
				return
			}
			resetAndClose()
			return
		}
		onOpenChange(next)
	}

	const isFlashMode = flashType !== null
	// Reopening a draft: the row is still on its way, so the form has nothing
	// to seed from yet.
	const isSeeding = isEditing && !draftProperty

	return (
		<>
		<Dialog open={open} onOpenChange={handleOpenChange} disablePointerDismissal>
			<DialogContent
				showCloseButton={false}
				onBackdropClick={() => handleOpenChange(false)}
				className={
					isFlashMode
						? 'sm:max-w-[34.875rem] w-full p-0 gap-0 rounded-lg overflow-hidden'
						: 'sm:max-w-183 w-full p-0 gap-0 rounded-lg overflow-hidden max-h-[90vh] flex flex-col'
				}
			>
				{isFlashMode ? (
					<PropertySavedFlash
						variant={flashType}
						propertyTitle={flashType === 'success' ? savedTitle : composePropertyTitle(form.getValues(), t)}
						errorDetail={errorDetail}
						onAction={() => {
							resetAndClose()
							// Черновик ведёт в раздел «Черновики», а не в карточку
							// объекта: незаполненную карточку убрали, и открывать
							// нечего — черновик дозаполняют в том же мастере,
							// который запускается из списка.
							if (flashType === 'draft') {
								void navigate({ to: '/properties/drafts' })
								return
							}
							if (flashType === 'success' && savedId) {
								void navigate({ to: '/properties/$propertyId', params: { propertyId: savedId } })
							}
						}}
						onClose={resetAndClose}
					/>
				) : (
					<>
						<div className="flex-shrink-0 flex flex-col gap-3 border-b border-(--color-border) bg-(--color-background) p-4">
							<div className="flex items-center gap-3 w-full">
								<DialogTitle className="flex-1 text-2xl leading-8 tracking-[-0.6px] font-[500] text-(--color-foreground) whitespace-nowrap">
									{t('properties.wizard.addTitle')}
								</DialogTitle>
								<IconButton
									variant="ghost"
									size="sm"
									type="button"
									onClick={() => { void handleWizardCloseClick() }}
									disabled={isDraftSaving}
									aria-label={t('common.close')}
								>
									<XIcon />
								</IconButton>
							</div>
							<Stages layout="wrap">
								{WIZARD_STEPS.map((label, i) => (
									<StageElement
										key={label}
										asDiv
										state={i <= step ? 'selected' : 'default'}
									>
										{label}
									</StageElement>
								))}
							</Stages>
						</div>

						{isSeeding ? (
							// The form must not be editable before the stored row has
							// arrived: hydration runs on the active=false → true
							// transition and would reset away anything typed first.
							<div className="flex-1 flex items-center justify-center p-12 text-(--color-muted-foreground)">
								{t('common.loading')}
							</div>
						) : (
						<Form {...form}>
							<form className="flex flex-col flex-1 min-h-0" onSubmit={(e) => { e.preventDefault(); void handleNext() }}>
								<div className="flex-1 overflow-y-auto">
									{step === 0 && <Step1Basics control={form.control} />}
									{step === 1 && <Step2Details />}
									{step === 2 && <Step3RentTerms />}
									{step === 3 && <Step4Utilities />}
									{step === 4 && <Step5AdditionalServices />}
									{step === 5 && <Step6PhotosDescription control={form.control} />}
								</div>

								<div className="flex-shrink-0 flex items-center justify-end gap-2 border-t border-(--color-border) bg-(--color-background) px-6 py-4">
									<Button
										type="button"
										variant="secondary"
										disabled={step === 0}
										onClick={handleBack}
									>
										Назад
									</Button>
									<Button type="submit" disabled={isSaving}>
										{step < 5 ? 'Далее' : isSaving ? 'Сохранение...' : 'Добавить объект'}
									</Button>
								</div>
							</form>
						</Form>
						)}
					</>
				)}
			</DialogContent>
		</Dialog>

		<ConfirmDialog
			open={showDiscardConfirm}
			onOpenChange={(v) => { if (!v) setShowDiscardConfirm(false) }}
			title="Закрыть без сохранения?"
			description="Заполненные данные будут потеряны."
			confirmLabel="Закрыть"
			cancelLabel="Отмена"
			variant="destructive"
			onConfirm={discardAndClose}
		/>
		</>
	)
}
